import { create } from "zustand";
import { devtools } from "zustand/middleware";
import axios from "axios";
import { IDrop } from "./useBeevisionDrops";

interface BeevisionAuditLog {
  _id: string;
  user: {
    _id: string;
    first_name: string;
    last_name: string;
  };
  machine: {
    _id: string;
    apiUrl: string;
  };
  beevisionDrop: any;
  status: "approved" | "rejected" | "pending" | "webhook_rejected";
  rejectionReason: string | null;
  webhookData: any;
  dropData: IDrop | null;
  createdAt: string;
  updatedAt: string;
}

interface Store {
  logs: BeevisionAuditLog[];
  total: number;
  page: number;
  limit: number;
  selectedLog: BeevisionAuditLog | null;
  measurementData: any | null;
  isMeasurementDataLoading: boolean;
  getLogs: (
    page?: number,
    limit?: number,
    status?: string,
    day?: number,
    month?: number,
    year?: number,
    locations?: string[],
    materials?: string[],
    search?: string
  ) => Promise<void>;
  getLogById: (id: string) => Promise<void>;
  setSelectedLog: (log: BeevisionAuditLog | null) => void;
  clearMeasurementData: () => void;
  downloadCsv: (
    status?: string,
    day?: number,
    month?: number,
    year?: number,
    locations?: string[],
    materials?: string[],
    search?: string
  ) => Promise<void>;
}

export const useBeevisionAuditLogs = create<Store>()(
  devtools((set, get) => ({
    logs: [],
    total: 0,
    page: 1,
    limit: 10,
    selectedLog: null,
    measurementData: null,
    isMeasurementDataLoading: false,

    getLogs: async (
      page = 1,
      limit = 10,
      status,
      day,
      month,
      year,
      locations = [],
      materials = [],
      search
    ) => {
      try {
        const { data } = await axios.get("/beevision-audit-logs", {
          // NOTE: backend expects comma-separated strings (then splits by ",")
          params: {
            page,
            limit,
            status,
            day,
            month,
            year,
            locations: locations.filter(Boolean).join(","),
            materials: materials.filter(Boolean).join(","),
            search: (search ?? "").trim() || undefined,
          },
        });
        set((state) => ({
          logs: page === 1 ? data.logs : [...state.logs, ...data.logs],
          total: data.total,
          page: data.page,
          limit: data.limit,
        }));
      } catch (error) {
        console.error("Error fetching beevision audit logs:", error);
      }
    },

    getLogById: async (id: string) => {
      try {
        const { data } = await axios.get(`/beevision-audit-logs/${id}`);
        set({ selectedLog: data });
        console.log({
          measurementId: data.webhookData?.MeasurementID,
          apiUrl: data.machine?.apiUrl,
        });

        if (data.webhookData?.MeasurementID && data.machine?.apiUrl) {
          set({ isMeasurementDataLoading: true });
          const measurementId = data.webhookData.MeasurementID;
          const apiUrl = data.machine.apiUrl;
          try {
            const { data: measurementData } = await axios.get(
              `/beevision-audit-logs/get-measurement?apiUrl=${apiUrl}&measurementId=${measurementId}`
            );
            set({ measurementData: measurementData });
          } catch (measurementError) {
            console.error("Error fetching measurement data:", measurementError);
          } finally {
            set({ isMeasurementDataLoading: false });
          }
        }
      } catch (error) {
        console.error(
          `Error fetching beevision audit log with id ${id}:`,
          error
        );
      }
    },

    setSelectedLog: (log) => {
      set({ selectedLog: log });
    },

    clearMeasurementData: () => {
      set({ measurementData: null });
    },

    downloadCsv: async (status, day, month, year, locations = [], materials = [], search) => {
      const { data } = await axios.get("/beevision-audit-logs/csv", {
        responseType: "blob",
        params: {
          status,
          day,
          month,
          year,
          locations: locations.filter(Boolean).join(","),
          materials: materials.filter(Boolean).join(","),
          search: (search ?? "").trim() || undefined,
        },
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "dropbot-audit-logs.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  }))
);
